/**
 * Animated favicon loader for long-running operations (e.g. AI quiz generation,
 * live-session start). Renders a pulsing amber Lumyn bulb to a `<link rel="icon">`
 * data URL while at least one task is active, then restores the original favicon
 * once every caller has released it.
 *
 * Usage:
 *   import { faviconLoader } from '@/utils/faviconLoader';
 *   const release = faviconLoader.start();
 *   try { await longTask(); } finally { release(); }
 *
 * The loader is ref-counted so overlapping calls are safe; the animation only
 * stops once every `start()` has been paired with its returned `release()`.
 */

const ICON_REL_MATCH = /icon/i;
const SIZE = 64;          // off-screen canvas resolution
const FRAME_MS = 90;      // ~11fps — enough for a smooth pulse, light on CPU

let activeCount = 0;
let intervalId = null;
let canvas = null;
let ctx = null;
let phase = 0;
let originalIcons = null; // [{ el, href }]
let dynamicLink = null;   // injected <link> we control

function captureOriginalIcons() {
  if (originalIcons) return;
  const links = Array.from(document.querySelectorAll('link[rel]'))
    .filter((l) => ICON_REL_MATCH.test(l.getAttribute('rel') || ''));
  originalIcons = links.map((el) => ({ el, href: el.getAttribute('href') }));
}

function ensureDynamicLink() {
  if (dynamicLink) return dynamicLink;
  // Disable existing favicons so the browser uses ours.
  for (const { el } of originalIcons || []) {
    el.setAttribute('data-favicon-prev-rel', el.getAttribute('rel') || 'icon');
    el.setAttribute('rel', 'prev-icon');
  }
  dynamicLink = document.createElement('link');
  dynamicLink.setAttribute('rel', 'icon');
  dynamicLink.setAttribute('type', 'image/png');
  dynamicLink.setAttribute('data-favicon-dynamic', 'true');
  document.head.appendChild(dynamicLink);
  return dynamicLink;
}

function restoreOriginalIcons() {
  if (dynamicLink && dynamicLink.parentNode) {
    dynamicLink.parentNode.removeChild(dynamicLink);
  }
  dynamicLink = null;
  for (const { el } of originalIcons || []) {
    const prev = el.getAttribute('data-favicon-prev-rel');
    if (prev) {
      el.setAttribute('rel', prev);
      el.removeAttribute('data-favicon-prev-rel');
    }
  }
  originalIcons = null;
}

function drawFrame() {
  if (!canvas) {
    canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    ctx = canvas.getContext('2d');
  }

  // Pulse intensity 0 → 1 → 0 (sine wave)
  const t = (Math.sin(phase) + 1) / 2; // 0..1
  phase += 0.18;

  ctx.clearRect(0, 0, SIZE, SIZE);

  // Rounded navy background — matches brand mark
  const r = 14;
  ctx.fillStyle = '#0E1B3D';
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.arcTo(SIZE, 0, SIZE, SIZE, r);
  ctx.arcTo(SIZE, SIZE, 0, SIZE, r);
  ctx.arcTo(0, SIZE, 0, 0, r);
  ctx.arcTo(0, 0, SIZE, 0, r);
  ctx.closePath();
  ctx.fill();

  // Outer glow (radial) — intensity follows pulse
  const glow = ctx.createRadialGradient(SIZE / 2, SIZE * 0.42, 4, SIZE / 2, SIZE * 0.42, SIZE * 0.55);
  glow.addColorStop(0, `rgba(251, 191, 36, ${0.45 + 0.5 * t})`);
  glow.addColorStop(0.55, `rgba(251, 191, 36, ${0.05 + 0.15 * t})`);
  glow.addColorStop(1, 'rgba(251, 191, 36, 0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, SIZE, SIZE);

  // Bulb (solid amber, brightness follows pulse)
  const amberAlpha = 0.7 + 0.3 * t;
  ctx.fillStyle = `rgba(251, 191, 36, ${amberAlpha})`;
  ctx.beginPath();
  // Same proportions as the static brand bulb, scaled to a 64×64 canvas.
  ctx.moveTo(32, 14);
  ctx.bezierCurveTo(23.2, 14, 16, 20.6, 16, 28.8);
  ctx.bezierCurveTo(16, 33.5, 18.4, 37.5, 22, 40);
  ctx.lineTo(22, 46);
  ctx.quadraticCurveTo(22, 47.1, 23.1, 47.1);
  ctx.lineTo(40.9, 47.1);
  ctx.quadraticCurveTo(42, 47.1, 42, 46);
  ctx.lineTo(42, 40);
  ctx.bezierCurveTo(45.6, 37.5, 48, 33.5, 48, 28.8);
  ctx.bezierCurveTo(48, 20.6, 40.8, 14, 32, 14);
  ctx.closePath();
  ctx.fill();

  // Base ridges
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(24, 50, 16, 3);
  ctx.fillRect(26, 55, 12, 2.5);

  const link = ensureDynamicLink();
  link.setAttribute('href', canvas.toDataURL('image/png'));
}

function tick() {
  try {
    drawFrame();
  } catch (e) {
    // Never let favicon issues bubble up — just stop animating.
    // eslint-disable-next-line no-console
    console.warn('faviconLoader frame error', e);
    hardStop();
  }
}

function hardStop() {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  restoreOriginalIcons();
  phase = 0;
  activeCount = 0;
}

export const faviconLoader = {
  /**
   * Begin pulsing the favicon. Returns a release function; call it (in a
   * `finally` block) to decrement the ref-count and restore the original
   * favicon once all callers have released.
   */
  start() {
    activeCount += 1;
    if (activeCount === 1) {
      captureOriginalIcons();
      // Render the first frame immediately so the user sees the switch.
      tick();
      intervalId = setInterval(tick, FRAME_MS);
    }
    let released = false;
    return () => {
      if (released) return;
      released = true;
      activeCount = Math.max(0, activeCount - 1);
      if (activeCount === 0) {
        hardStop();
      }
    };
  },

  /** Force-stop and restore — safety hatch if a caller forgets to release. */
  reset() {
    hardStop();
  },
};

export default faviconLoader;
